import {TaxExemptionDocument} from 'common'
import {applicationStatus} from 'common/enums'
import AlgoliaClient, {
  SALE_TAX_EXEMPTION_ADMIN_PANEL_ALGOLIA_INDEX
} from 'components/AlgoliaSearch'
import InputField from 'components/InputField'
import Pagination from 'components/Pagination'
import ReqNotFoundScreen from 'components/ReqNotFoundScreen'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import {Timestamp} from 'firebase/firestore'
import debounce from 'lodash/debounce'
import isDate from 'lodash/isDate'
import last from 'lodash/last'
import startCase from 'lodash/startCase'
import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import Layout from '../Layout'
import {Loader} from '../Loader'
import NavBar from '../Navbar'
import withAuthorization from '../Session/withAuthorization'
import Table from '../TableComponent'
import {convertToDate, getFormattedDate, searchWithAlgolia} from '../utils'
import styles from './TaxExemptionApplications.module.scss'

const TaxExemptionApplications = ({customClass}: {customClass: string}) => {
  const navigate = useNavigate()
  const {state} = useLocation()
  const pageFromLocation = state?.page
  const [loader, setLoader] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [pageCount, setPageCount] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [taxExemptionData, setTaxExemptionData] = useState<
    Array<TaxExemptionDocument>
  >([])
  const [dir, setDir] = useState<{key: string; dir: string}>({
    key: '',
    dir: 'asc'
  })

  const searchIndex = AlgoliaClient.initIndex(
    SALE_TAX_EXEMPTION_ADMIN_PANEL_ALGOLIA_INDEX
  )

  const debouncedSearch = React.useRef(
    debounce(searchWithAlgolia<TaxExemptionDocument>, 800)
  )

  useEffect(() => {
    searchIndex
      .setSettings({
        customRanking: ['desc(lastUpdated)']
      })
      .then(() => {
        AlgoliaClient.clearCache()
        searchWithAlgolia<TaxExemptionDocument>({
          searchQuery: '*',
          searchIndex,
          setLoaderState: setLoader,
          setData: setTaxExemptionData,
          setPageCount,
          currentPage,
          sortData: false
        }).then(() => setLoader(false))
      })
  }, [])

  const sortBy = async (keys: Array<string>) => {
    await AlgoliaClient.clearCache()
    setLoader(true)
    let newDir = 'asc'
    if (dir.key === keys[0]) {
      newDir = dir.dir === 'asc' ? 'desc' : 'asc'
    }
    setDir({dir: newDir, key: keys[0]})
    await searchIndex.setSettings({
      customRanking: keys.map((key) => newDir + '(' + key + ')')
    })
    setCurrentPage(1)
    setTimeout(() => {
      debouncedSearch?.current({
        searchQuery: searchValue,
        searchIndex,
        setLoaderState: setLoader,
        setData: setTaxExemptionData,
        setPageCount,
        currentPage: 1,
        sortData: false
      })
    }, 1000)
  }

  const tableTitles = [
    {title: 'Account Name', sortBy: () => sortBy(['accountName'])},
    {title: 'Contact Name', sortBy: () => sortBy(['contactName'])},
    {title: 'Account ID'},
    {title: 'Contact ID'},
    {
      title: 'Application Status',
      sortBy: () => sortBy(['certificates.status'])
    },
    {title: 'Last Modified', sortBy: () => sortBy(['lastUpdated'])}
  ]

  const getFormattedData = (taxExemptionData: TaxExemptionDocument) => {
    const {
      contactName,
      accountName,
      contactId,
      accountId,
      certificates,
      lastUpdated: lastUpdatedAt
    } = taxExemptionData
    const lastUpdatedDate = lastUpdatedAt && convertToDate(lastUpdatedAt as any)
    const status = startCase(last(certificates)?.status) as applicationStatus
    const lastUpdated = isDate(lastUpdatedDate)
      ? getFormattedDate(lastUpdatedDate)
      : '-'
    const date = isDate(lastUpdatedDate)
      ? Timestamp.fromDate(lastUpdatedDate).seconds
      : '-'
    return {
      contactId,
      accountId,
      contactName,
      accountName,
      status,
      lastUpdated,
      date,
      sortDate:
        (lastUpdatedDate && Timestamp.fromDate(lastUpdatedDate).seconds) || 0
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    setSearchValue(value)
    if (value) {
      debouncedSearch?.current({
        searchQuery: value,
        searchIndex,
        setLoaderState: setLoader,
        setData: setTaxExemptionData,
        setPageCount,
        sortData: false
      })
    } else {
      debouncedSearch?.current({
        searchQuery: '*',
        searchIndex,
        setLoaderState: setLoader,
        setData: setTaxExemptionData,
        setPageCount,
        currentPage,
        sortData: false
      })
    }
  }

  const getRowData = (
    rowData: Array<{
      contactId: string
      contactName: string
      accountId: string
      accountName: string
      status: applicationStatus
      lastUpdated: string
      date: string | number
    }>
  ) => {
    return (rowData || []).map((singleRowData, index) => {
      const {
        contactName,
        accountName,
        status,
        lastUpdated,
        contactId,
        accountId
      } = singleRowData
      return (
        <tr
          key={`tax-exemption-row-data-${index}`}
          onClick={() => {
            navigate(
              '/tax-exemption-applications/tax-exemption-application-details',
              {
                state: {id: accountId + "-" + contactId, currentPage},
                preventScrollReset: true
              }
            )
          }}
        >
          <td>{accountName || '-'}</td>
          <td>{contactName || '-'}</td>
          <td>{contactId || '-'}</td>
          <td>{accountId || '-'}</td>
          <td>{status || '-'}</td>
          <td>{lastUpdated || '-'}</td>
        </tr>
      )
    })
  }

  const noData = !taxExemptionData.length && !loader

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const items = taxExemptionData.map((data) => getFormattedData(data))

  const showPagination = pageCount > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setLoader(true)
    setCurrentPage(page)
    debouncedSearch?.current({
      searchQuery: searchValue,
      searchIndex,
      setLoaderState: setLoader,
      setData: setTaxExemptionData,
      setPageCount,
      currentPage: page,
      sortData: false
    })
    setLoader(false)
    scrollToRef()
  }

  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={[styles.container, customClass].join(' ')}>
        <NavBar title="Tax Exemption Applications" />
        <div className={styles.button_filters}>
          <InputField
            name="taxExemptionSearchField"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            customClass={styles.inputField}
            placeholder="Search for Filters"
            isSearch={true}
          />
        </div>
        {loader ? (
          <Loader />
        ) : noData ? (
          <ReqNotFoundScreen />
        ) : (
          <React.Fragment>
            <Table titles={tableTitles} rowData={getRowData(items)} />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageCount}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(TaxExemptionApplications)
